import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Save, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { LocationSelect } from "@/components/LocationSelect";
import { GROUPS, eur, reportPdf } from "@/lib/reportPdf";

const EMPTY = { group_by: ["wine", "vintage"], q: "", vintage: "", country: "", region: "", color: "", location_id: "", include_pending: false };

export default function Reports() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(EMPTY);
  const [rep, setRep] = useState(null);
  const [saved, setSaved] = useState([]);
  const [name, setName] = useState("");
  const set = (k) => (v) => setCfg((c) => ({ ...c, [k]: v }));

  const run = useCallback(() => {
    if (!cfg.group_by.length) return setRep(null);
    api.post("/reports/run", cfg).then((r) => setRep(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [cfg]);
  useEffect(() => {
    const t = setTimeout(run, 300);
    return () => clearTimeout(t);
  }, [run]);
  const loadSaved = () => api.get("/reports/saved").then((r) => setSaved(r.data)).catch(() => {});
  useEffect(() => {
    loadSaved();
  }, []);

  const toggleGroup = (g) => {
    if (!cfg.group_by.includes(g) && cfg.group_by.length >= 4) return toast.error("Up to 4 groupings");
    set("group_by")(cfg.group_by.includes(g) ? cfg.group_by.filter((x) => x !== g) : [...cfg.group_by, g]);
  };
  const exportXlsx = async () => {
    try {
      const { data } = await api.post("/reports/export", cfg, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = `${name || "cellar-listing"}.xlsx`;
      a.click();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const save = async () => {
    if (!name.trim()) return toast.error("Give the listing a name");
    try {
      await api.post("/reports/saved", { ...cfg, name });
      toast.success("Listing saved");
      loadSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const open = (s) => {
    setCfg(Object.fromEntries(Object.keys(EMPTY).map((k) => [k, s[k] ?? EMPTY[k]])));
    setName(s.name);
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete the listing "${s.name}"?`)) return;
    try {
      await api.delete(`/reports/saved/${s.id}`);
      toast.success("Listing deleted");
    } catch (e) {
      toast.error(errMsg(e));
    }
    loadSaved();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12" data-testid="reports-page">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Listings</p>
      <h2 className="font-serif text-4xl sm:text-5xl text-[#F5F0EB] mb-8">Build a listing</h2>
      {!!saved.length && <SavedList saved={saved} onOpen={open} onRemove={remove} />}
      <GroupPicker groups={cfg.group_by} onToggle={toggleGroup} />
      <Filters cfg={cfg} set={set} />
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Listing name" maxLength={80} className="h-10 w-56" data-testid="report-name-input" />
        <Button variant="outline" onClick={save} data-testid="report-save-button"><Save className="w-4 h-4 mr-2" />Save listing</Button>
        <Button variant="outline" onClick={exportXlsx} disabled={!rep} data-testid="report-excel-button"><Download className="w-4 h-4 mr-2" />Excel</Button>
        <Button variant="outline" onClick={() => reportPdf(rep, name, user?.cave_name)} disabled={!rep} data-testid="report-pdf-button"><FileText className="w-4 h-4 mr-2" />PDF / print</Button>
      </div>
      {rep && <ReportTable rep={rep} />}
    </div>
  );
}

const SavedList = ({ saved, onOpen, onRemove }) => (
  <div className="flex flex-wrap gap-2 mb-6" data-testid="saved-reports">
    {saved.map((s) => (
      <span key={s.id} className="flex items-center rounded-full border border-[#4A3B43] bg-[#161214] text-sm">
        <button onClick={() => onOpen(s)} className="pl-3 pr-2 py-1.5 text-[#F5F0EB] hover:text-[#D4AF37]" data-testid={`saved-report-${s.id}`}>{s.name}</button>
        <button onClick={() => onRemove(s)} className="pr-2.5 text-[#6E665D] hover:text-[#8C2B36]" aria-label="Delete listing" data-testid={`delete-saved-report-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
      </span>
    ))}
  </div>
);

const GroupPicker = ({ groups, onToggle }) => (
  <div className="mb-5">
    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6E665D] mb-2">Group by (in the order you tap, up to 4)</p>
    <div className="flex flex-wrap gap-2">
      {Object.entries(GROUPS).map(([k, label]) => {
        const i = groups.indexOf(k);
        return (
          <button key={k} onClick={() => onToggle(k)} aria-pressed={i >= 0} data-testid={`group-${k}`}
            className={`rounded-full px-4 h-9 text-sm border transition-colors ${i >= 0 ? "bg-[#D4AF37] border-[#D4AF37] text-[#0D0A0B]" : "border-[#4A3B43] text-[#A89F91] hover:border-[#D4AF37]"}`}>
            {i >= 0 && <span className="font-mono mr-1.5">{i + 1}</span>}{label}
          </button>
        );
      })}
    </div>
  </div>
);

const Filters = ({ cfg, set }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-5" data-testid="report-filters">
    <Input value={cfg.q} onChange={(e) => set("q")(e.target.value)} placeholder="Wine or producer" className="h-10 col-span-2" data-testid="report-filter-q" />
    <Input value={cfg.vintage} onChange={(e) => set("vintage")(e.target.value)} placeholder="Vintage or NV" className="h-10" data-testid="report-filter-vintage" />
    <Input value={cfg.country} onChange={(e) => set("country")(e.target.value)} placeholder="Country" className="h-10" data-testid="report-filter-country" />
    <Input value={cfg.region} onChange={(e) => set("region")(e.target.value)} placeholder="Region" className="h-10" data-testid="report-filter-region" />
    <Input value={cfg.color} onChange={(e) => set("color")(e.target.value)} placeholder="Colour" className="h-10" data-testid="report-filter-color" />
    <LocationSelect value={cfg.location_id} onChange={set("location_id")} allowAll testId="report-filter-location" className="h-10" />
    <label className="flex items-center gap-2 text-sm text-[#A89F91]">
      <Switch checked={cfg.include_pending} onCheckedChange={set("include_pending")} data-testid="report-filter-pending" />Include pending
    </label>
  </div>
);

const ReportTable = ({ rep }) => (
  <div className="rounded-xl border border-[#2E252A] overflow-x-auto" data-testid="report-table">
    <table className="w-full text-sm">
      <thead className="bg-[#161214] text-left">
        <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E665D]">
          {rep.group_by.map((g) => <th key={g} className="px-4 py-3">{GROUPS[g]}</th>)}
          {["Wines", "Bottles", "Boxes", "Value"].map((h) => <th key={h} className="px-4 py-3 text-right">{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rep.rows.map((r, i) => (
          <tr key={i} className="border-t border-[#2E252A] hover:bg-[#161214]" data-testid={`report-row-${i}`}>
            {rep.group_by.map((g) => <td key={g} className={`px-4 py-2.5 text-[#F5F0EB] ${g === "wine" ? "min-w-[220px]" : "whitespace-nowrap"}`}>{r[g]}</td>)}
            <td className="px-4 py-2.5 text-right font-mono">{r.wines}</td>
            <td className="px-4 py-2.5 text-right font-mono text-[#F5F0EB]">{r.bottles}</td>
            <td className="px-4 py-2.5 text-right font-mono">{r.boxes}</td>
            <td className="px-4 py-2.5 text-right font-mono">{eur(r.value)}</td>
          </tr>
        ))}
        {!rep.rows.length && <tr><td colSpan={rep.group_by.length + 4} className="px-4 py-10 text-center text-[#6E665D]">No bottles match these filters.</td></tr>}
      </tbody>
      <tfoot className="bg-[#161214] border-t border-[#4A3B43]">
        <tr className="text-[#D4AF37]" data-testid="report-totals">
          <td className="px-4 py-3" colSpan={rep.group_by.length}>Total</td>
          <td className="px-4 py-3 text-right font-mono">{rep.totals.wines}</td>
          <td className="px-4 py-3 text-right font-mono">{rep.totals.bottles}</td>
          <td className="px-4 py-3 text-right font-mono">{rep.totals.boxes}</td>
          <td className="px-4 py-3 text-right font-mono">{eur(rep.totals.value)}</td>
        </tr>
      </tfoot>
    </table>
  </div>
);
